import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Download } from 'lucide-react';
import { toPng } from 'html-to-image';
import { classes as classesData } from '../character/content/classes';
import type { DhClass, ClassFeature, ClassStats, ClassInventory, CharacterDescription, Subclass, MartialFormData } from '../character/content/classes';
import equipmentData from '../character/data/equipment.json';
import { DomainIcon } from '../character/cards/DomainIcon';
import { parseTextWithTerms, parseCardTextWithTerms } from '../character/content/parseText';
import { TermLink } from '../components/TermLink';
import { useStrings } from '../locales';
import '../components/CardSheet.css';
import { SwipeHint } from '../components/CardSheet';
import './Classes.css';
import { useNewSourceFilter } from '../hooks/useNewSourceFilter';
import { useContentVersion } from '../hooks/ContentVersionContext';
import { pickVersion, sourceLabel, matchesSource, SOURCE_OPTIONS } from '../character/content/sources';

interface Weapon {
  name: string;
  tier: number;
  trait: string;
  range: string;
  damage: string;
  damageType: string;
  category: string;
  feature?: string;
  isUnique?: boolean;
  burden?: string;
}

interface Armor {
  name: string;
  tier: number;
  major: string | number;
  severe: string | number;
  score: string | number;
  feature?: string;
  isUnique?: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────
const DOMAIN_CLASS_MAP: Record<string, string[]> = {
  Assassin: ['Blade', 'Midnight'],
  Bard: ['Grace', 'Codex'],
  Brawler: ['Bone', 'Valor'],
  "Blood Hunter": ['Blade', 'Blood'],
  Druid: ['Arcana', 'Sage'],
  Guardian: ['Blade', 'Valor'],
  Ranger: ['Bone', 'Sage'],
  Rogue: ['Grace', 'Midnight'],
  Seraph: ['Splendor', 'Valor'],
  Sorcerer: ['Arcana', 'Midnight'],
  Warlock: ['Dread', 'Grace'],
  Warrior: ['Blade', 'Bone'],
  Witch: ['Dread', 'Sage'],
  Wizard: ['Codex', 'Splendor'],
};

const EQUIP_TRAIT_COLORS: Record<string, string> = {
  Strength: '#ff7070', Agility: '#60d890', Finesse: '#f06ab0',
  Instinct: '#6aaeff', Presence: '#c47fff', Knowledge: '#e8c547',
};

const TRAIT_NAMES = ['Agility', 'Strength', 'Finesse', 'Instinct', 'Presence', 'Knowledge'];

// ── Helper components ─────────────────────────────────────────────────
interface DomainBadgeProps { domain: string; }
function DomainBadge({ domain }: DomainBadgeProps) {
  const cls = domain.toLowerCase().replace(/[^a-z]/g, '');
  return <span className={`domain-badge domain-${cls}`}>{domain}</span>;
}

interface FeatureBlockProps {
  feature: ClassFeature | string;
  accent?: string;
}
function FeatureBlock({ feature, accent }: FeatureBlockProps) {
  const color = accent || 'var(--gold)';
  const featureObj = typeof feature === 'string' ? { description: feature } : feature;
  return (
    <div className="feature-block-cls" style={{ '--feature-accent': color } as React.CSSProperties}>
      {featureObj.name && (
        <div className="feature-block-cls__name">{featureObj.name}</div>
      )}
      <div className="feature-block-cls__body">{parseTextWithTerms(featureObj.description || (featureObj as unknown as string))}</div>
    </div>
  );
}

const TIER_COLORS: Record<string, string> = {
  foundation: '#c9a227',
  specialization: '#e8c547',
  mastery: '#9b5fdd',
};

interface TierCardData {
  clsName: string;
  sub: Subclass;
  tierKey: string;
  tierLabel: string;
  items: Array<ClassFeature | string>;
}

// ── Single card (grid view) ───────────────────────────────────────────
function SubclassTierGridCard({ data, domains, preview }: { data: TierCardData; domains: string[]; preview?: boolean }) {
  const { clsName, sub, tierKey, tierLabel, items } = data;
  const color = TIER_COLORS[tierKey] || '#888';
  return (
    <div className="portrait-card" style={{ '--domain-color': color } as React.CSSProperties}>
      {domains[0] && (
        <DomainIcon domain={domains[0]} className="sub-tier-card__icon sub-tier-card__icon--left" aria-hidden />
      )}
      {domains[1] && (
        <DomainIcon domain={domains[1]} className="sub-tier-card__icon sub-tier-card__icon--right" aria-hidden />
      )}
      <div className="card-sheet__type">{tierLabel}</div>
      <h2 className="card-sheet__name">{sub.name}</h2>
      {sub.spellcastTrait && (
        <div className="sub-tier-spellcast">Spellcast · <TermLink>{sub.spellcastTrait}</TermLink></div>
      )}
      <div className="card-sheet__rule" aria-hidden="true" />
      <div className="card-sheet__body">
        {items.map((item, i) => {
          const feat = typeof item === 'string' ? { name: null, description: item } : item;
          return (
            <div key={i} className="card-feature">
              {feat.name && <span className="card-feature__name">{feat.name}</span>}
              <div className="card-feature__text">{parseCardTextWithTerms(feat.description || '')}</div>
            </div>
          );
        })}
      </div>
      <div className="card-sheet__footer">
        <span>{clsName}</span>
        <span className="card-sheet__footer-dot">·</span>
        <span>{sub.name}</span>
        <span className="card-sheet__footer-dot">·</span>
        <span>{tierLabel}</span>
        <span className="card-sheet__footer-dot">·</span>
        <span>dhtools.net</span>
      </div>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────
function SubclassTierModal({ items, idx, domains, onClose, onNav }: {
  items: TierCardData[];
  idx: number;
  domains: string[];
  onClose: () => void;
  onNav: (i: number) => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const isDragging = useRef(false);

  const prev = useCallback(() => onNav((idx - 1 + items.length) % items.length), [idx, items.length, onNav]);
  const next = useCallback(() => onNav((idx + 1) % items.length), [idx, items.length, onNav]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
    if (cardRef.current) cardRef.current.style.transition = 'none';
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !cardRef.current) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    cardRef.current.style.transform = `translateX(${Math.max(-60, Math.min(60, dx))}px)`;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging.current || !cardRef.current) return;
    isDragging.current = false;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    cardRef.current.style.transition = '';
    cardRef.current.style.transform = '';
    if (dx < -60) next();
    else if (dx > 60) prev();
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  const handleDownload = useCallback(async () => {
    const wrapper = cardRef.current;
    if (!wrapper) return;
    const card = wrapper.querySelector<HTMLElement>('.portrait-card') ?? wrapper;

    type UseRestore = { svgEl: SVGSVGElement; savedHTML: string; savedViewBox: string | null };
    const restores: UseRestore[] = [];
    card.querySelectorAll<SVGUseElement>('use').forEach((useEl) => {
      const href = useEl.getAttribute('href') || useEl.getAttribute('xlink:href') || '';
      if (!href.startsWith('#')) return;
      const symbol = document.querySelector<SVGSymbolElement>(href);
      const svgEl = useEl.closest<SVGSVGElement>('svg');
      if (!symbol || !svgEl) return;
      const savedHTML = svgEl.innerHTML;
      const savedViewBox = svgEl.getAttribute('viewBox');
      restores.push({ svgEl, savedHTML, savedViewBox });
      const vb = symbol.getAttribute('viewBox');
      if (vb) svgEl.setAttribute('viewBox', vb);
      svgEl.innerHTML = symbol.innerHTML;
    });

    try {
      const url = await toPng(card, { pixelRatio: Math.max(window.devicePixelRatio, 2), width: card.offsetWidth, height: card.offsetHeight });
      const a = document.createElement('a');
      a.download = `${items[idx].sub.name.replace(/\s+/g, '-').toLowerCase()}-${items[idx].tierKey}.png`;
      a.href = url; a.click();
    } finally {
      restores.forEach(({ svgEl, savedHTML, savedViewBox }) => {
        svgEl.innerHTML = savedHTML;
        if (savedViewBox) svgEl.setAttribute('viewBox', savedViewBox);
        else svgEl.removeAttribute('viewBox');
      });
    }
  }, [items, idx]);

  return createPortal(
    <div className="card-modal-overlay" onClick={onClose}>
      <button className="card-nav-btn card-nav-btn--prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous">
        <ChevronLeft size={22} />
      </button>
      <SwipeHint />
      <div ref={cardRef} className="card-modal-card-wrapper" onClick={(e) => e.stopPropagation()} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <SubclassTierGridCard data={items[idx]} domains={domains} />
        <button className="card-sheet__close" onClick={onClose} aria-label="Close"><X size={14} /></button>
      </div>
      <button className="card-download-btn" onClick={(e) => { e.stopPropagation(); handleDownload(); }} aria-label="Download" title="Download card">
        <Download size={16} /> Download
      </button>
      <button className="card-nav-btn card-nav-btn--next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next">
        <ChevronRight size={22} />
      </button>
    </div>,
    document.body
  );
}

// ── Subclass card (toggle) with tier card grid inside ────────────────
interface SubclassCardProps { sub: Subclass; domains: string[]; clsName: string; }
function SubclassCard({ sub, domains, clsName }: SubclassCardProps) {
  const s = useStrings().classes;
  const [open, setOpen] = useState<boolean>(false);
  const [openTierIdx, setOpenTierIdx] = useState<number | null>(null);

  const tierCards: TierCardData[] = ([
    { clsName, sub, tierKey: 'foundation', tierLabel: s.subclassTiers.foundation, items: sub.foundation },
    { clsName, sub, tierKey: 'specialization', tierLabel: s.subclassTiers.specialization, items: sub.specialization },
    { clsName, sub, tierKey: 'mastery', tierLabel: s.subclassTiers.mastery, items: sub.mastery },
  ] as TierCardData[]).filter((t) => t.items && t.items.length > 0);

  return (
    <div className="subclass-card">
      <button className="subclass-card__toggle" onClick={() => setOpen((o) => !o)}>
        <div>
          <div className="subclass-card__name">{sub.name}</div>
          {sub.spellcastTrait && (
            <div className="subclass-card__spellcast">{s.spellcast} <TermLink>{sub.spellcastTrait}</TermLink></div>
          )}
        </div>
        <span className="subclass-card__chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="subclass-card__body">
          <div className="card-grid">
            {tierCards.map((data, i) => (
              <button key={data.tierKey} className="card-btn" onClick={() => setOpenTierIdx(i)} aria-label={`Open ${data.tierLabel}`}>
                <SubclassTierGridCard data={data} domains={domains} preview />
              </button>
            ))}
          </div>
          {sub.martialForm && <MartialFormSection form={sub.martialForm} />}
          {openTierIdx !== null && (
            <SubclassTierModal items={tierCards} idx={openTierIdx} domains={domains} onClose={() => setOpenTierIdx(null)} onNav={setOpenTierIdx} />
          )}
        </div>
      )}
    </div>
  );
}

function MartialFormSection({ form }: { form: MartialFormData }) {
  return (
    <div className="martial-form-section">
      <div className="martial-form-section__header">{form.title ?? 'Martial Form Stances'}</div>
      <p className="martial-form-section__desc">{form.description}</p>
      {Object.entries(form.tiers).map(([tier, stances]) => (
        <div key={tier} className="martial-form-tier">
          <div className="martial-form-tier__label">Tier {tier}</div>
          <div className="martial-form-stances">
            {stances.map((s) => (
              <div key={s.name} className="martial-form-stance">
                <span className="martial-form-stance__name">{s.name}.</span>
                <span className="martial-form-stance__desc"> {s.description}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


interface TraitSpreadProps { raw: string; }
function TraitSpread({ raw }: TraitSpreadProps) {
  const s = useStrings().classes;
  const values = raw.split(',').map(v => v.trim());
  return (
    <div>
      <div className="trait-spread__label">{s.stats.traitSpread}</div>
      <div className="trait-spread__chips">
        {TRAIT_NAMES.map((name, i) => {
          const val = values[i] || '0';
          const isPos = val.startsWith('+') || parseInt(val, 10) > 0;
          const isNeg = val.startsWith('-') || parseInt(val, 10) < 0;
          const borderColor = isPos ? 'var(--hope)' : isNeg ? 'var(--fear)' : 'var(--border)';
          const valueColor = isPos ? 'var(--hope)' : isNeg ? 'var(--fear)' : 'var(--text-muted)';
          return (
            <div key={name} className="trait-chip" style={{ '--trait-border': borderColor, '--trait-value-color': valueColor } as React.CSSProperties}>
              <div className="trait-chip__name">{name}</div>
              <div className="trait-chip__value">{val}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface EquipLinkProps {
  name: string;
  tab: 'weapons' | 'armor';
  fromClass?: string;
}
function EquipLink({ name, tab, fromClass }: EquipLinkProps) {
  const s = useStrings().classes;
  const [show, setShow] = useState<boolean>(false);
  const item: Weapon | Armor | undefined = tab === 'weapons'
    ? (equipmentData.weapons as Weapon[]).find((w) => w.name === name)
    : (equipmentData.armor as Armor[]).find((a) => a.name === name);

  // Class-granted weapons (e.g. Brawler's Strike) are not in the equipment list.
  if (!item) return <span className="equip-link-plain">{name}</span>;

  const weaponItem = tab === 'weapons' ? item as Weapon | undefined : undefined;
  const topColor = tab === 'weapons'
    ? (EQUIP_TRAIT_COLORS[weaponItem?.trait || ''] || 'var(--gold)')
    : 'var(--gold)';

  return (
    <div
      className="equip-link-wrap"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Link
        to={`/equipment?tab=${tab}&q=${encodeURIComponent(name)}`}
        state={fromClass ? { backTo: `/classes/${fromClass.toLowerCase()}`, backLabel: fromClass } : undefined}
        className="equip-link"
      >
        {name}
      </Link>

      {show && item && (
        <div className="equip-tooltip" style={{ '--equip-top-color': topColor } as React.CSSProperties}>
          <div className="equip-tooltip__name">
            {item.name}
            {item.isUnique && <span className="equip-tooltip__named">{s.tooltip.named}</span>}
          </div>

          {tab === 'weapons' && weaponItem ? (
            <div className="equip-tooltip__grid-2">
              {[
                { label: 'Tier', value: `T${weaponItem.tier}` },
                { label: 'Trait', value: weaponItem.trait, color: EQUIP_TRAIT_COLORS[weaponItem.trait] },
                { label: 'Range', value: weaponItem.range },
                { label: 'Damage', value: weaponItem.damage, color: weaponItem.damageType === 'Magical' ? '#c47fff' : 'var(--gold)', mono: true },
              ].map(({ label, value, color, mono }) => (
                <div key={label}>
                  <div className="equip-tooltip__stat-label">{label}</div>
                  <div className={`equip-tooltip__stat-value${mono ? ' equip-tooltip__stat-value--mono' : ''}`} style={color ? { '--stat-color': color } as React.CSSProperties : {}}>
                    {value}
                    {label === 'Damage' && (
                      <span className="equip-tooltip__damage-type">
                        {weaponItem.damageType === 'Magical' ? 'mag' : 'phy'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            (() => {
              const armorItem = item as Armor;
              return (
                <div className="equip-tooltip__grid-4">
                  {[
                    { label: 'Tier', value: `T${armorItem.tier}` },
                    { label: 'Major', value: armorItem.major, color: 'var(--gold)' },
                    { label: 'Severe', value: armorItem.severe, color: 'var(--fear)' },
                    { label: 'Score', value: armorItem.score },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="equip-tooltip__armor-stat">
                      <div className="equip-tooltip__armor-label">{label}</div>
                      <div className="equip-tooltip__armor-value" style={color ? { '--stat-color': color } as React.CSSProperties : {}}>{value}</div>
                    </div>
                  ))}
                </div>
              );
            })()
          )}

          {item.feature && (
            <div className="equip-tooltip__feature">
              <span className="equip-tooltip__feature-name">{item.feature.split(':')[0]}: </span>
              {item.feature.split(':').slice(1).join(':').trim()}
            </div>
          )}
          <div className="equip-tooltip__cta">{s.tooltip.viewCta}</div>
        </div>
      )}
    </div>
  );
}

interface LoadoutRow {
  label: string;
  value: string | string[];
  tab: 'weapons' | 'armor';
}

interface SuggestedLoadoutProps {
  stats: ClassStats;
  fromClass: string;
}
function SuggestedLoadout({ stats, fromClass }: SuggestedLoadoutProps) {
  const s = useStrings().classes;
  const rows: LoadoutRow[] = ([
    stats.suggestedPrimary ? { label: String(s.suggestedLoadout.primary), value: stats.suggestedPrimary, tab: 'weapons' as const } : null,
    stats.suggestedSecondary ? { label: String(s.suggestedLoadout.secondary), value: stats.suggestedSecondary, tab: 'weapons' as const } : null,
    stats.suggestedArmor ? { label: String(s.suggestedLoadout.armor), value: stats.suggestedArmor, tab: 'armor' as const } : null,
  ] as Array<LoadoutRow | null>).filter((r): r is LoadoutRow => r !== null);

  if (rows.length === 0 && !stats.suggestedTraits) return null;
  return (
    <div className="suggested-loadout">
      <div className="suggested-loadout__title">{s.suggestedLoadout.title}</div>
      {rows.length > 0 && (
        <div className="suggested-loadout__grid" style={{ marginBottom: stats.suggestedTraits ? '14px' : 0 }}>
          {rows.map(r => (
            <div key={r.label}>
              <div className="suggested-loadout__row-label">{r.label}</div>
              {Array.isArray(r.value)
                ? <div className="equip-link-multi">{r.value.map((v) => <EquipLink key={v} name={v} tab={r.tab} fromClass={fromClass} />)}</div>
                : <EquipLink name={r.value} tab={r.tab} fromClass={fromClass} />
              }
            </div>
          ))}
        </div>
      )}
      {stats.suggestedTraits && <TraitSpread raw={stats.suggestedTraits} />}
    </div>
  );
}

function InventorySection({ inv }: { inv: ClassInventory }) {
  return (
    <div className="char-guide-box char-guide-box--inventory">
      <div className="char-guide-box__title">Inventory</div>
      <div className="inventory-block">
        <div className="inventory-label">Take</div>
        <p className="inventory-text">{inv.take}</p>
        <div className="inventory-label">Then choose between</div>
        <p className="inventory-text">
          {inv.chooseBetween[0]} <span className="inventory-or">or</span> {inv.chooseBetween[1]}
        </p>
        <div className="inventory-label">And either</div>
        <p className="inventory-text">
          {inv.andEither[0]} <span className="inventory-or">or</span> {inv.andEither[1]}
        </p>
      </div>
      {inv.thenDecideWhatYouUseForYourCraft?.length ? (
        <>
          <div className="inventory-label">Craft</div>
          <p className="inventory-text">
            {inv.thenDecideWhatYouUseForYourCraft.join(", ")}
          </p>
        </>
      ) : null}
    </div>
  );
}

function CharDescSection({ desc }: { desc: CharacterDescription }) {
  const lines: Array<{ label: string; items: string[] }> = [
    { label: 'Clothes that are', items: desc.clothes },
    { label: 'Eyes like', items: desc.eyes },
    { label: "Body that's", items: desc.body },
    { label: 'Skin the color of', items: desc.skin },
    { label: 'Attitude like', items: desc.attitude },
  ];
  return (
    <div className="char-guide-box char-guide-box--desc">
      <div className="char-guide-box__title">Character Description</div>
      <p className="char-guide-box__hint">Choose one (or more) from each line, or write your own description.</p>
      <div className="char-desc-lines">
        {lines.map(({ label, items }) => (
          <div key={label} className="char-desc-line">
            <span className="char-desc-line__label">{label}:</span>{' '}
            <span className="char-desc-line__items">{items.join(', ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface ClassDetailProps { cls: DhClass; }
function ClassDetail({ cls }: ClassDetailProps) {
  const navigate = useNavigate();
  const s = useStrings().classes;
  const domains = DOMAIN_CLASS_MAP[cls.name] || [];

  return (
    <div className="detail-view">
      {/* Fixed background domain icons */}
      {domains[0] && (
        <DomainIcon domain={domains[0]} className="cls-bg-icon cls-bg-icon--left" aria-hidden />
      )}
      {domains[1] && (
        <DomainIcon domain={domains[1]} className="cls-bg-icon cls-bg-icon--right" aria-hidden />
      )}

      <button className="back-btn" onClick={() => navigate('/classes')}>
        {s.backToClasses}
      </button>

      <div className="detail-header cls-detail-header">
        {domains.map((d, i) => (
          <DomainIcon
            key={d}
            domain={d}
            className="cls-detail-art"
            style={{ right: `${-10 + (domains.length - 1 - i) * 85}px` } as React.CSSProperties}
          />
        ))}
        <div className="detail-title">
          <h1>{cls.name}</h1>
          <div className="detail-category">
            {s.stats.domains}&nbsp;
            {domains.map((d) => <DomainBadge key={d} domain={d} />)}
          </div>
        </div>
      </div>

      <div className="stats-row">
        {cls.stats?.evasion && (
          <div className="stat-chip">
            <div className="stat-label">{s.stats.baseEvasion}</div>
            <div className="stat-value">{cls.stats.evasion}</div>
          </div>
        )}
        {cls.stats?.hp && (
          <div className="stat-chip">
            <div className="stat-label">{s.stats.startingHp}</div>
            <div className="stat-value">{cls.stats.hp}</div>
          </div>
        )}
        {cls.stats?.stress && (
          <div className="stat-chip">
            <div className="stat-label">Stress</div>
            <div className="stat-value">{cls.stats.stress}</div>
          </div>
        )}
      </div>

      <div className="cls-sheet-links">
        <Link className="cls-sheet-link cls-sheet-link--primary" to={`/character-creation?class=${encodeURIComponent(cls.name)}`}>
          Create a {cls.name}
        </Link>
        <Link className="cls-sheet-link" to={`/character-creation?blank=${encodeURIComponent(cls.name)}`}>
          Printable {cls.name} sheet
        </Link>
      </div>

      {(cls.inventory || cls.characterDescription) && (
        <div className="section-block">
          <div className="two-col-detail">
            {cls.inventory && <InventorySection inv={cls.inventory} />}
            {cls.characterDescription && <CharDescSection desc={cls.characterDescription} />}
          </div>
        </div>
      )}

      {!cls.inventory && cls.items && (
        <div className="cls-starting-items">
          <span className="cls-starting-items__label">{s.startingItems}</span>
          {cls.items}
        </div>
      )}

      {cls.description && cls.description.length > 0 && (
        <div className="section-block">
          <h2>{s.sections.about}</h2>
          {cls.description.map((d, i) => <p key={i}>{parseTextWithTerms(d)}</p>)}
        </div>
      )}

      {cls.stats && <SuggestedLoadout stats={cls.stats} fromClass={cls.name} />}

      {cls.hopeFeature && (
        <div className="section-block">
          <h2>{s.sections.hopeFeature}</h2>
          <FeatureBlock
            feature={(() => {
              const colonIdx = cls.hopeFeature!.indexOf(':');
              if (colonIdx > 0 && colonIdx < 60) {
                return { name: cls.hopeFeature!.substring(0, colonIdx), description: cls.hopeFeature!.substring(colonIdx + 1).trim() };
              }
              return { description: cls.hopeFeature };
            })()}
            accent="var(--hope)"
          />
        </div>
      )}

      {cls.classFeatures && cls.classFeatures.length > 0 && (
        <div className="section-block">
          <h2>{cls.classFeatures.length === 1 ? s.sections.classFeature : s.sections.classFeatures}</h2>
          {cls.classFeatures.map((f, i) => (
            <FeatureBlock key={i} feature={f} accent="var(--gold)" />
          ))}
        </div>
      )}

      {Object.keys(cls.subclasses || {}).length > 0 && (
        <div className="section-block">
          <h2>{s.sections.subclasses}</h2>
          <p className="cls-subclass-note">{s.subclassNote}</p>
          {Object.values(cls.subclasses!).map((sub) => (
            <SubclassCard key={sub.name} sub={sub} domains={domains} clsName={cls.name} />
          ))}
        </div>
      )}

      {((cls.backgroundQuestions && cls.backgroundQuestions.length > 0) || (cls.connectionQuestions && cls.connectionQuestions.length > 0)) && (
        <div className="section-block">
          <h2>{s.sections.characterGuide}</h2>
          <div className="two-col-detail">
            {cls.backgroundQuestions && cls.backgroundQuestions.length > 0 && (
              <div className="char-guide-box" style={{ '--guide-accent': 'var(--gold)' } as React.CSSProperties}>
                <div className="char-guide-box__title">{s.charGuide.backgroundTitle}</div>
                <p className="char-guide-box__hint">{s.charGuide.backgroundHint}</p>
                <ol className="char-guide-box__list">
                  {cls.backgroundQuestions.map((q, i) => (
                    <li key={i} className="char-guide-box__item">{q}</li>
                  ))}
                </ol>
              </div>
            )}
            {cls.connectionQuestions && cls.connectionQuestions.length > 0 && (
              <div className="char-guide-box" style={{ '--guide-accent': 'var(--purple-light)' } as React.CSSProperties}>
                <div className="char-guide-box__title">{s.charGuide.connectionsTitle}</div>
                <p className="char-guide-box__hint">{s.charGuide.connectionsHint}</p>
                <ol className="char-guide-box__list">
                  {cls.connectionQuestions.map((q, i) => (
                    <li key={i} className="char-guide-box__item">{q}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default function Classes() {
  const { className } = useParams<{ className?: string }>();
  const s = useStrings().classes;
  const [filter, setFilter] = useState<string>('All');
  const [sourceFilter, setSource] = useState<string>('All');
  const { glows, onSourceClick } = useNewSourceFilter('/classes');
  const [version] = useContentVersion();
  const visibleClasses = pickVersion(classesData, version);

  if (className) {
    const cls = visibleClasses.find((c) => c.name.toLowerCase() === className);
    if (!cls) return <div className="empty-state"><h3>{s.notFound}</h3></div>;
    return <ClassDetail cls={cls} />;
  }

  const domainFilters = ['All', 'Arcana', 'Blade', 'Blood', 'Bone', 'Codex', 'Dread', 'Grace', 'Midnight', 'Sage', 'Splendor', 'Valor'];
  const filtered = visibleClasses.filter((c) => {
    if (filter !== 'All' && !(DOMAIN_CLASS_MAP[c.name] || []).some((d) => d === filter)) return false;
    return matchesSource(sourceFilter, c.source);
  });

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>{s.title}</h1>
        <p>{s.desc}</p>
      </div>

      {/* Mobile: compact dropdown */}
      <select
        className="cls-domain-select"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        aria-label={s.filterDomainAria}
      >
        {domainFilters.map((d) => (
          <option key={d} value={d}>{d === 'All' ? s.allDomains : d}</option>
        ))}
      </select>

      {/* Desktop: domain pill bar */}
      <div className="filter-bar cls-filter-bar">
        <span className="filter-label">{s.filterDomainLabel}</span>
        {domainFilters.map((d) => (
          <button
            key={d}
            className={`filter-btn${filter === d ? ' active' : ''}`}
            onClick={() => setFilter(d)}
          >
            {d}
          </button>
        ))}
      </div>

      {/* Source filter */}
      <div className="filter-bar cls-filter-bar">
        <span className="filter-label">Source</span>
        {SOURCE_OPTIONS.map((src) => (
          <button
            key={src}
            className={`filter-btn${sourceFilter === src ? ' active' : ''}${glows(src) ? ' new-source-glow' : ''}`}
            onClick={() => { setSource(src); onSourceClick(src); }}
          >
            {src}
          </button>
        ))}
      </div>

      <div className="card-grid classes-grid">
        {filtered.map((cls) => {
          const domains = DOMAIN_CLASS_MAP[cls.name] || [];
          const subclassNames = Object.keys(cls.subclasses || {});
          return (
            <Link key={cls.name} to={`/classes/${cls.name.toLowerCase()}`} className="card cls-grid-card">
              {domains.map((d, i) => (
                <DomainIcon
                  key={d}
                  domain={d}
                  className="cls-grid-card__art"
                  style={{ right: `${5 + (domains.length - 1 - i) * 80}px` } as React.CSSProperties}
                />
              ))}
              <div className="card-title">{cls.name}</div>
              <div className="card-tags">
                {domains.map((d) => <DomainBadge key={d} domain={d} />)}
              </div>
              <div className="card-body" style={{ marginTop: '10px' }}>
                {cls.description?.[0]?.substring(0, 110)}...
              </div>
              <div className="cls-card-stats">
                {(cls.stats?.evasion || cls.stats?.hp || cls.stats?.stress) && (
                  <div className="cls-card-stat-row" style={{ marginBottom: subclassNames.length > 0 ? '5px' : 0 }}>
                    {cls.stats?.evasion && <span>Evasion {cls.stats.evasion}</span>}
                    {cls.stats?.hp && <span>HP {cls.stats.hp}</span>}
                    {cls.stats?.stress && <span>Stress {cls.stats.stress}</span>}
                  </div>
                )}
                {subclassNames.length > 0 && (
                  <div className="cls-subclass-list">
                    {Object.values(cls.subclasses!).map((sub) => (
                      <div key={sub.name} className="cls-subclass-row">
                        <span className="cls-subclass-name">{sub.name}</span>
                        {sub.spellcastTrait && (
                          <span className="cls-subclass-trait">{sub.spellcastTrait}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <span className="source-label">{sourceLabel(cls.source)}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
